import { createSafeTriangleGate } from './safe-triangle.js';

export function initNavMenu(scope) {
  const roots = (scope || document).querySelectorAll(".etp-nav-menu");

  roots.forEach((nav) => {
    if (nav.dataset.etpNavMenuReady) return;
    nav.dataset.etpNavMenuReady = "true";

    const level1 = nav.querySelector(".level-1");
    const level2Items = nav.querySelectorAll(".menu-item-depth-1");
    const panels = nav.querySelectorAll(".mega-panel");
    const level3Lists = nav.querySelectorAll(".level-3");

    if (!level1) return;

    // --------------------------------------------------
    // HEIGHT CALCULATION
    // --------------------------------------------------

    const updateHeight = () => {
      if (!panels.length) return;

      let maxHeight = 0;

      const measure = (el) => {
        if (!el) return;
        const prevDisplay = el.style.display;
        const prevVisibility = el.style.visibility;

        // Ensure measurable
        el.style.visibility = "hidden";
        el.style.display = "block";

        const height = el.offsetHeight;

        el.style.display = prevDisplay;
        el.style.visibility = prevVisibility;

        if (height > maxHeight) maxHeight = height;
      };

      measure(level1);

      panels.forEach((panel) => measure(panel));
      level3Lists.forEach((list) => measure(list));

      const minHeight = parseInt(
        window.getComputedStyle(nav).minHeight || 0,
        10
      );

      nav.style.height = Math.max(maxHeight, minHeight) + "px";
    };

    // --------------------------------------------------
    // STATE MANAGEMENT
    // --------------------------------------------------

    const deactivateAll = () => {
      level2Items.forEach((item) => {
        item.classList.remove("is-active");

        const link = item.querySelector("a[aria-expanded]");
        if (link) link.setAttribute("aria-expanded", "false");
      });
    };

    // Clear the JS-driven container height. Only call this when the menu is
    // truly closing — otherwise the .level-1 column collapses to its natural
    // height and the divider shrinks while the panel is still visible.
    const resetHeight = () => {
      nav.style.height = "";
    };

    const deactivateAllDepth0 = () => {
      nav.querySelectorAll(".menu-item-depth-0.is-active").forEach((el) => {
        el.classList.remove("is-active");
      });
    };

    const activateItem = (item) => {
      deactivateAll();

      if (!item.classList.contains("menu-item-has-children")) {
        return;
      }

      item.classList.add("is-active");

      const link = item.querySelector("a[aria-expanded]");
      if (link) link.setAttribute("aria-expanded", "true");

      updateHeight();
    };

    // Cascade: activate the current-page Level-1 item (or first as fallback),
    // then activate the current-page Level-2 item if on a relevant page.
    // Fallback: only show Level 2 panel, no Level-2 active state and no Level 3.
    const activateFirstCascade = () => {
      const currentDepth0 =
        nav.querySelector(".menu-item-depth-0.current-menu-ancestor") ||
        nav.querySelector(".menu-item-depth-0.current-menu-item");

      const activeDepth0 =
        currentDepth0 ||
        nav.querySelector(".menu-item-depth-0.menu-item-has-children");

      if (!activeDepth0) return;

      activeDepth0.classList.add("is-active");

      if (!currentDepth0) return;

      const panel = activeDepth0.querySelector(".mega-panel");
      if (!panel) return;

      const activeDepth1 =
        panel.querySelector(".menu-item-depth-1.current-menu-ancestor") ||
        panel.querySelector(".menu-item-depth-1.current-menu-item");

      if (activeDepth1) {
        activateItem(activeDepth1);
      }
    };

    // --------------------------------------------------
    // HOVER + FOCUS
    // --------------------------------------------------

    // For each mega-panel, set up a safe-triangle gate scoped to its L2
    // column. The gate suppresses mouseenter activation on sibling L2 items
    // while the cursor is on a smooth diagonal toward the currently open L3.
    panels.forEach((panel) => {
      const level2Col = panel.querySelector(".level-2");
      const gateL2ToL3 = level2Col
        ? createSafeTriangleGate({
            scope: level2Col,
            getTarget: () => {
              const activeL2 = panel.querySelector(".menu-item-depth-1.is-active");
              return activeL2 ? activeL2.querySelector(".level-3") : null;
            },
            direction: "right",
          })
        : () => false;

      panel.querySelectorAll(".menu-item-depth-1").forEach((item) => {
        const link = item.querySelector("a");
        if (!link) return;

        item.addEventListener("mouseenter", () => {
          if (gateL2ToL3()) return;
          activateItem(item);
        });

        link.addEventListener("focus", () => {
          activateItem(item);
        });
      });
    });

    // --------------------------------------------------
    // AUTO ACTIVATE FIRST LEVEL-2 ITEM ON LEVEL-1 HOVER
    // --------------------------------------------------

    const level1Items = nav.querySelectorAll(".menu-item-depth-0");

    // Safe-triangle gate for the L1 column: when the cursor is moving
    // diagonally from a hovered L1 toward the currently open mega-panel,
    // skip activation on whatever sibling L1 the path crosses.
    const gateL1ToPanel = createSafeTriangleGate({
      scope: level1,
      getTarget: () => {
        const activeL1 = nav.querySelector(".menu-item-depth-0.is-active");
        return activeL1 ? activeL1.querySelector(".mega-panel") : null;
      },
      direction: "right",
    });

    level1Items.forEach((item) => {
      item.addEventListener("mouseenter", () => {
        if (gateL1ToPanel()) return;

        deactivateAllDepth0();

        const panel = item.querySelector(".mega-panel");
        if (!panel) return;

        const firstWithChildren = panel.querySelector(
          ".menu-item-depth-1.menu-item-has-children"
        );

        if (firstWithChildren) {
          activateItem(firstWithChildren);
        }
      });
    });

    // --------------------------------------------------
    // CASCADE ON PARENT (BRICKS DROPDOWN) HOVER
    // --------------------------------------------------

    const dropdownContent = nav.closest(".brx-dropdown-content");
    const dropdownParent = dropdownContent?.parentElement;

    if (dropdownParent) {
      dropdownParent.addEventListener("mouseenter", activateFirstCascade);

      dropdownParent.addEventListener("mouseleave", () => {
        deactivateAllDepth0();
        deactivateAll();
        resetHeight();
      });
    }

    // --------------------------------------------------
    // CLOSE ON PANEL LEAVE
    // --------------------------------------------------

    panels.forEach((panel) => {
      panel.addEventListener("mouseleave", () => {
        deactivateAll();
      });
    });

    // --------------------------------------------------
    // ESC SUPPORT
    // --------------------------------------------------

    nav.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        deactivateAll();
        resetHeight();
      }
    });

    // --------------------------------------------------
    // AUTO ACTIVATE FIRST
    // --------------------------------------------------

    const firstWithChildren = nav.querySelector(
      ".menu-item-depth-1.menu-item-has-children"
    );

    if (firstWithChildren) {
      activateItem(firstWithChildren);
    } else {
      updateHeight();
    }

    // --------------------------------------------------
    // RESIZE OBSERVER
    // --------------------------------------------------

    if ("ResizeObserver" in window) {
      const observer = new ResizeObserver(() => {
        updateHeight();
      });

      observer.observe(nav);
    }
  });
}
